import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import LiveStreamPanel from '../components/LiveStreamPanel.jsx'
import MetricsPanel from '../components/MetricsPanel.jsx'
import NotificationCenter from '../components/NotificationCenter.jsx'
import StoredApiKeyManager from '../components/StoredApiKeyManager.jsx'
import StatementsPanel from '../components/StatementsPanel.jsx'
import LegacyActivityStream from '../activity-stream/app.jsx'
import { NotificationProvider } from '../hooks/useNotifications.jsx'
import { StreamsProvider, useStreams } from './StreamsProvider.jsx'
import useStoredApiKeys from '../hooks/useStoredApiKeys.js'
import { clearCredentials, selectAuthState, setCredentials } from './store/authSlice.js'
import { formatLastUsed, maskKeyPreview } from '../activity-stream/apiKeyUtils.js'

const ConnectionIndicator = ({ isConnected }) => (
  <span
    className="nav-connection-indicator"
    style={{
      display: 'inline-block',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: isConnected ? '#22c55e' : '#ef4444',
      marginLeft: '6px',
    }}
    aria-label={isConnected ? 'Connected' : 'Disconnected'}
  />
)

const ActivityStreamWrapper = ({ onStoredKeyUsed, isVisible }) => {
  const { activityStream } = useStreams()

  return (
    <div style={{ display: isVisible ? 'contents' : 'none' }}>
      <LegacyActivityStream
        onStoredKeyUsed={onStoredKeyUsed}
        externalStream={activityStream}
      />
    </div>
  )
}

const Navigation = () => {
  const { activityStream, liveMonitorStream, statementsStream, metricsStream } = useStreams()

  const navItems = useMemo(() => {
    const getConnectionState = (path) => {
      switch (path) {
        case '/activity':
          return activityStream.connection === 'connected'
        case '/statements':
          return statementsStream.connectionState === 'connected'
        case '/live':
          return liveMonitorStream.connectionState === 'connected'
        case '/metrics':
          return metricsStream.connectionState === 'connected'
        default:
          return false
      }
    }

    return [
      { path: '/activity', label: 'Activity Stream', isConnected: getConnectionState('/activity') },
      { path: '/statements', label: 'Statements', isConnected: getConnectionState('/statements') },
      { path: '/live', label: 'Live Monitor', isConnected: getConnectionState('/live') },
      { path: '/metrics', label: 'Metrics', isConnected: getConnectionState('/metrics') },
    ]
  }, [
    activityStream.connection,
    statementsStream.connectionState,
    liveMonitorStream.connectionState,
    metricsStream.connectionState,
  ])

  return (
    <nav className="app-shell__nav" aria-label="Primary navigation">
      {navItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          className={({ isActive }) => (isActive ? 'active' : '')}
        >
          {item.label}
          <ConnectionIndicator isConnected={item.isConnected} />
        </NavLink>
      ))}
    </nav>
  )
}

const App = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { apiKey, writeKey, isAuthenticated } = useSelector(selectAuthState)

  const {
    keys: storedKeys,
    activeKey: storedActiveKey,
    activeKeyId: storedActiveKeyId,
    setActiveKeyId: setStoredActiveKeyId,
    clearActiveSelection,
    addKey: addStoredKey,
    updateKey: updateStoredKey,
    deleteKey: deleteStoredKey,
    touchKey: touchStoredKey,
  } = useStoredApiKeys()

  const [isManagerOpen, setIsManagerOpen] = useState(false)

  useEffect(() => {
    const desiredApiKey = storedActiveKey?.apiKey ?? ''
    const desiredWriteKey = storedActiveKey?.writeKey ?? ''

    console.log('[App] Redux sync check:', {
      hasStoredKey: Boolean(storedActiveKey),
      desiredApiKey: desiredApiKey ? '***present***' : 'empty',
      currentReduxApiKey: apiKey ? '***present***' : 'empty',
      needsSync: Boolean(desiredApiKey && (desiredApiKey !== apiKey || desiredWriteKey !== (writeKey ?? '')))
    })

    if (desiredApiKey) {
      if (desiredApiKey !== apiKey || desiredWriteKey !== (writeKey ?? '')) {
        console.log('[App] Dispatching credentials to Redux...')
        dispatch(setCredentials({ apiKey: desiredApiKey, writeKey: desiredWriteKey }))
      }
    } else if (apiKey || writeKey) {
      console.log('[App] Clearing credentials from Redux...')
      dispatch(clearCredentials())
    }
  }, [storedActiveKey?.apiKey, storedActiveKey?.writeKey, apiKey, writeKey, dispatch])

  const handleStoredSelection = useCallback(
    (event) => {
      const nextId = event.target.value || null
      setStoredActiveKeyId(nextId)
    },
    [setStoredActiveKeyId],
  )

  const handleManageKeys = useCallback(() => {
    setIsManagerOpen(true)
  }, [])

  const handleManagerClose = useCallback(() => {
    setIsManagerOpen(false)
  }, [])

  const handleManagerSelect = useCallback(
    (id) => {
      setStoredActiveKeyId(id)
      setIsManagerOpen(false)
    },
    [setStoredActiveKeyId],
  )

  const handleSignOut = useCallback(() => {
    clearActiveSelection()
    dispatch(clearCredentials())
  }, [clearActiveSelection, dispatch])

  const handleStoredKeyUsed = useCallback(() => {
    if (storedActiveKey?.id) {
      touchStoredKey(storedActiveKey.id)
    }
  }, [storedActiveKey?.id, touchStoredKey])


  // Check if user needs API key for protected routes
  useEffect(() => {
    const isActivityStreamRoute = location.pathname === '/' || location.pathname === '/activity'
    if (!isActivityStreamRoute && !storedActiveKey) {
      setIsManagerOpen(true)
    }
  }, [location.pathname, storedActiveKey])

  const hasStoredKeys = storedKeys.length > 0


  return (
    <NotificationProvider>
      <StreamsProvider onStoredKeyUsed={handleStoredKeyUsed}>
        <div className="app-shell">
          <header className="app-shell__header">
            <Navigation />
            <div className="app-shell__actions">
              <div className="app-shell__action-buttons">
                <select value={storedActiveKeyId ?? ''} onChange={handleStoredSelection}>
                  <option value="">Offline</option>
                  {storedKeys.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label || `Key ${entry.id.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
                <button type="button" className="secondary" onClick={handleManageKeys}>
                  Manage keys
                </button>
              </div>
            </div>
          </header>

          <main className="app-shell__main app-shell__main--legacy">
            <section className="app-shell__content app-shell__content--legacy">
              <Routes>
                <Route path="/" element={<Navigate to="/activity" replace />} />
                <Route path="/activity" element={<div style={{ display: 'contents' }} />} />
                <Route path="/live" element={<div style={{ display: 'contents' }} />} />
                <Route path="/metrics" element={<div style={{ display: 'contents' }} />} />
                <Route path="/statements" element={<div style={{ display: 'contents' }} />} />
              </Routes>
            </section>
            {/* ActivityStream is always mounted to preserve state and SSE connection */}
            <ActivityStreamWrapper
              onStoredKeyUsed={handleStoredKeyUsed}
              isVisible={location.pathname === '/activity' || location.pathname === '/'}
            />
            {/* LiveStreamPanel is always mounted to preserve state and SSE connection */}
            <div style={{ display: location.pathname === '/live' ? 'contents' : 'none' }}>
              <LiveStreamPanel />
            </div>
            {/* StatementsPanel is always mounted to preserve state and SSE connection */}
            <div style={{ display: location.pathname === '/statements' ? 'contents' : 'none' }}>
              <StatementsPanel />
            </div>
            {/* MetricsPanel is always mounted to preserve state and SSE connection */}
            <div style={{ display: location.pathname === '/metrics' ? 'contents' : 'none' }}>
              <MetricsPanel />
            </div>
          </main>

          <NotificationCenter />
        </div>
        <StoredApiKeyManager
          isOpen={isManagerOpen}
          keys={storedKeys}
          activeKeyId={storedActiveKeyId}
          onSelect={handleManagerSelect}
          onAdd={addStoredKey}
          onUpdate={updateStoredKey}
          onDelete={deleteStoredKey}
          onClose={handleManagerClose}
        />
      </StreamsProvider>
    </NotificationProvider>
  )
}

export default App
